import React, { useCallback, useContext } from 'react'
import styled from 'styled-components'
import { rem } from 'polished'

import { useScreenSizeOnly } from 'hooks/useScreenSize'
import PreviewableModal from '../../../../Tripkit/PreviewableModal/PreviewableModal'
import DesktopOfferMapCard from './DesktopOfferMapCard'
import ModalHeader from 'components/Luxkit/Modal/ModalHeader'
import ModalBody from 'components/Luxkit/Modal/ModalBody'
import ModalFooter from 'components/Luxkit/Modal/ModalFooter'
import ModalContent from 'components/Luxkit/Modal/ModalContent'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import BodyTextBlock from 'components/Luxkit/TextBlocks/BodyTextBlock'
import LineMapMarkerIcon from 'components/Luxkit/Icons/line/LineMapMarkerIcon'
import LargeContentToggle from 'components/Common/LargeContentToggle'
import CardCarousel from 'components/Luxkit/Carousel/CardCarousel'
import Image from 'components/Common/Image'
import Group from 'components/utils/Group'
import TextButton from 'components/Luxkit/Button/TextButton'
import offerPageURL from 'lib/offer/offerPageURL'
import useImmersiveModeHandlers from 'tripPlanner/hooks/useImmersiveModeHandlers'
import TripContext from 'tripPlanner/contexts/TripContext'
import HeadingTextBlock from 'components/Luxkit/TextBlocks/HeadingTextBlock'
import { HotelBookmarkButton } from 'tripPlanner/components/Bookmark/BookmarkButton'
import { AccommodationItem } from 'tripPlanner/types/tripItem'
import { buildMyEscapesOrderLink, formatShortDateRange, isLeBooking, isLeBookmark } from 'tripPlanner/utils'
import { BOOKING_DETAILS_COPY } from 'tripPlanner/config'
import SavedItemRemoveButton from 'tripPlanner/components/ItemCard/Common/SavedItemRemoveButton'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import BookedLabel from 'tripPlanner/components/BookedLabel'
import NotesAndAttachments from './NotesAndAttachments'
import Divider from 'components/Luxkit/Divider'
import LEOfferPriceDetails from 'tripPlanner/components/ItemOfferPrice/LEOfferPriceDetails'
import { findHotelPackage } from 'tripPlanner/utils/offer'
import OfferInclusionItem from 'components/Common/Inclusions/OfferInclusionItem'
import { nonNullable } from 'lib/array/arrayUtils'
import useAccommodationItemOccupancyDescription from 'tripPlanner/hooks/occupancy/useAccommodationItemOccupancyDescription'
import { useRegionTimeFormat } from 'hooks/useRegionTimeFormat'
import LineClockIcon from 'components/Luxkit/Icons/line/LineClockIcon'
import LineNotesIcon from 'components/Luxkit/Icons/line/LineNotesIcon'
import MarkdownRender from 'components/Luxkit/MarkdownRender'

const SrcImage = styled.img`
  height: ${rem(120)};
  width: auto;
`

const CarouselContainer = styled.div`
  padding-bottom: ${rem(20)};
`

const Flex1 = styled.div`
  flex: 1;
`

interface Props {
  offer: App.HotelOffer
  tripItem?: AccommodationItem
  onClose: () => void
}

function LEHotelSummaryCard({ offer, tripItem, onClose }: Props) {
  const isMobile = useScreenSizeOnly('mobile')
  const { setImmersiveMode } = useImmersiveModeHandlers()
  const tripContext = useContext(TripContext)
  const tripId = tripContext?.tripId
  const canEdit = tripContext?.permissions.canEdit ?? false
  const viewAsTemplate = tripContext?.viewAsTemplate ?? false

  const onClickViewOffer = useCallback(() => {
    if (tripId) {
      setImmersiveMode({
        tripId,
      })
    }
  }, [tripId, setImmersiveMode])

  const carousel = <CardCarousel gap={8} gutterSize={20} desktopGutterSize={24} gutterStyle="overflow">
    {offer.images.map(image => (
      image.id ?
        <Image key={image.id} id={image.id} alt={image.title} height={120} /> :
        <SrcImage key={image.url} src={image.url} alt={image.title} />
    ))}
  </CardCarousel>

  const bookingID = tripItem && isLeBooking(tripItem) && canEdit && !viewAsTemplate ? tripItem.confirmationCode : undefined

  const bookmarkButton = tripItem ? <SavedItemRemoveButton item={tripItem} /> : <HotelBookmarkButton offer={offer} iconOnly size="small" />

  const timeFormat = useRegionTimeFormat()
  const { occupanciesText } = useAccommodationItemOccupancyDescription(tripItem)
  let timeAndOccupancyText: string | undefined = undefined
  let checkInText: string | undefined = undefined
  if (tripItem) {
    if (tripItem.startTime) {
      checkInText = isLeBookmark(tripItem) ?
        `Check-in from ${tripItem.startTime.format(timeFormat)}` :
        `Check-in at ${tripItem.startTime.format(timeFormat)}`
    }
    const timeDescription = formatShortDateRange(tripItem)
    timeAndOccupancyText = nonNullable([timeDescription, occupanciesText])
      .filter((text) => text.length > 0)
      .join(', ')
  }

  const mainContent = <>
    <Group direction="horizontal" gap={8}>
      <Flex1>
        <VerticalSpacer gap={8}>
          {checkInText && (
            <BodyTextBlock variant="medium" colour="neutral-three" startIcon={<LineClockIcon />}>
              {checkInText}
            </BodyTextBlock>
          )}
          {offer.property.address && (
            <BodyTextBlock
              variant="medium"
              startIcon={<LineMapMarkerIcon />}
              weight="bold"
              wrap="word-wrap"
            >
              {offer.property.address}
            </BodyTextBlock>
          )}
          {timeAndOccupancyText && (
            <BodyTextBlock variant="medium" startIcon={<LineNotesIcon />}>
              {timeAndOccupancyText}
            </BodyTextBlock>
          )}
          {tripItem?.isBooked && <CSSBreakpoint max="mobile">
            <BookedLabel />
          </CSSBreakpoint>}
        </VerticalSpacer>
      </Flex1>
      <CSSBreakpoint max="mobile">
        {bookmarkButton}
      </CSSBreakpoint>
    </Group>

    <LargeContentToggle
      maxHeight={80}
      collapsedHeight={60}
      showMoreText="Read more"
      showLessText="Read less"
      showChevron={false}
    >
      <VerticalSpacer gap={12}>
        {offer.tileInclusions && offer.tileInclusions.length > 0 && (
          <VerticalSpacer gap={2}>
            {offer.tileInclusions.map(inclusion => (
              <OfferInclusionItem
                key={inclusion.id}
                inclusion={inclusion}
                isHighlighted={inclusion.isHighlighted}
                showMinStayDescription
              />
            ))}
          </VerticalSpacer>
        )}
        <MarkdownRender type="compact" content={offer.description} fontSize="medium" />
      </VerticalSpacer>
    </LargeContentToggle>

    {tripItem && <>
      <Divider kind="primary" />
      <NotesAndAttachments item={tripItem} canEdit={canEdit} />
    </>}
  </>

  const bookingLink = !!bookingID && tripItem && <TextButton
    kind="secondary"
    size="large"
    to={buildMyEscapesOrderLink(tripItem)}
  >
    {BOOKING_DETAILS_COPY}
  </TextButton>

  const footer = <ModalFooter>
    <Group
      direction={bookingLink ? 'vertical' : 'horizontal'}
      desktopDirection="horizontal"
      horizontalAlign="space-between"
      verticalAlign={bookingLink ? 'start' : 'center'}
      desktopVerticalAlign="center"
      gap={12}
      wrap="wrap"
    >
      {!tripItem?.isBooked &&
        <LEOfferPriceDetails
          offer={offer}
          package={tripItem && findHotelPackage(offer.packages, tripItem)}
        />
      }

      <Group direction="horizontal" horizontalAlign="center" gap={12} wrap="wrap">
        <TextButton kind="tertiary" size="large" to={offerPageURL(offer)} onClick={onClickViewOffer}>
          View offer
        </TextButton>
        {bookingLink}
      </Group>
    </Group>
  </ModalFooter>

  if (isMobile) {
    return (
      <PreviewableModal onRequestClose={onClose}>
        <ModalHeader onCloseButtonClick={onClose} title={offer.property.name} />
        <ModalBody>
          <ModalContent>
            <CarouselContainer>
              {carousel}
            </CarouselContainer>
            <VerticalSpacer gap={20}>
              {mainContent}
            </VerticalSpacer>
          </ModalContent>
        </ModalBody>
        {footer}
      </PreviewableModal>
    )
  }

  return (
    <DesktopOfferMapCard>
      <ModalBody>
        <ModalContent>
          <CarouselContainer>
            {carousel}
          </CarouselContainer>
          <VerticalSpacer gap={20}>
            <HeadingTextBlock
              variant="heading5"
              endIcon={<Group direction="horizontal" gap={12} verticalAlign="center">
                {tripItem?.isBooked && <BookedLabel />}
                {bookmarkButton}
              </Group>}
              fit="stretch"
            >
              {offer.property.name}
            </HeadingTextBlock>
            {mainContent}
          </VerticalSpacer>
        </ModalContent>
      </ModalBody>
      {footer}
    </DesktopOfferMapCard>
  )
}

export default LEHotelSummaryCard
